import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react'

import { semanticTokens } from './semantic'
import { tokens } from './tokens'

/**
 * Scout's design system.
 *
 * Everything brand-specific belongs here rather than scattered across
 * components: define a token once, reference it by name everywhere. The
 * config below extends Chakra's defaults, so anything not overridden still
 * comes from upstream.
 *
 * The one rule that matters outside this directory: components reference
 * semantic tokens (`bg`, `fg`, `border`, `colorPalette`) and never raw scale
 * values like `forest.700`. That is what keeps adding a dark palette to an
 * edit of `./semantic` rather than a sweep through every component.
 */
const config = defineConfig({
  globalCss: {
    'html, body': {
      // Chakra resets margins; this keeps the app filling the viewport.
      minHeight: '100dvh',
    },
    body: {
      // Chakra does not paint the body by default. Without this, dark mode
      // puts near-white text on a white page.
      bg: 'bg',
      color: 'fg',
      fontFamily: 'body',
      // Body sits at 400 and 1.5; the UI role tightens from there where it is
      // used. See the Typography section of the change's design.md.
      fontWeight: 'normal',
      lineHeight: 'body',
    },
    // The UI role: buttons, inputs and labels carry a touch of negative
    // tracking that body text does not.
    'button, input, select, textarea, label': {
      letterSpacing: 'ui',
    },

    /*
     * Scrollbars, in the application's own colours.
     *
     * The platform's default is a grey bar sized for a document, and this
     * application is mostly panels that scroll independently — a conversation
     * beside a record beside a contract. Three chrome-grey bars against a warm
     * off-white page is where a careful surface stops looking like one thing.
     *
     * Semantic tokens rather than fixed greys, so the dark palette is an edit to
     * `./semantic` like everything else rather than a second rule here.
     *
     * Both syntaxes, because they are two different standards rather than a
     * vendor prefix: Firefox implements `scrollbar-width` and `scrollbar-color`,
     * WebKit and Blink implement the `::-webkit-scrollbar` pseudo-elements, and
     * neither reads the other. The track stays transparent in both so the bar
     * sits on whatever surface it is over rather than cutting a channel in it.
     */
    '*': {
      scrollbarWidth: 'thin',
      scrollbarColor: '{colors.border.emphasized} transparent',
    },
    '::-webkit-scrollbar': {
      width: '10px',
      height: '10px',
    },
    '::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      background: '{colors.border}',
      borderRadius: 'full',
      // A transparent border clipped to the content box is what makes the
      // thumb narrower than the gutter it travels in, so it reads as a bar on
      // the page rather than as an edge of it.
      border: '3px solid transparent',
      backgroundClip: 'content-box',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: '{colors.border.emphasized}',
      backgroundClip: 'content-box',
    },
    '::-webkit-scrollbar-corner': {
      background: 'transparent',
    },
  },

  theme: {
    keyframes: {
      /**
       * A light passing across text that is still being written.
       *
       * For work that is genuinely running — the same rule a spinner is held
       * to. It sweeps right to left because the text it crosses is read left
       * to right, so the highlight arrives where the eye has not been yet
       * rather than chasing it.
       *
       * Paired with a gradient twice the width of its box, so the sweep has
       * somewhere to travel from and to.
       */
      shimmer: {
        from: { backgroundPosition: '200% center' },
        to: { backgroundPosition: '0% center' },
      },
    },
    tokens,
    semanticTokens,
  },
})

export const system = createSystem(defaultConfig, config)
